import React, { useState } from 'react';
import { FileText, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';

interface ExamTypeSelectorProps {
  examType: 'OB' | 'Blind' | 'GYN' | 'General' | null;
  studyCategory: 'T1' | 'T2' | 'T3' | null;
  gestationalAge: { weeks: number; days: number; totalDays: number } | null;
  onExamTypeChange: (type: 'OB' | 'Blind' | 'GYN' | 'General') => void;
  onStudyCategoryChange: (category: 'T1' | 'T2' | 'T3') => void;
  getDefaultCategory: (lmpDate: string) => 'T1' | 'T2' | 'T3' | null;
  suggestedLMP?: string | null;
  pregnancyData?: any;
  studyExists?: boolean;
}

export const ExamTypeSelector: React.FC<ExamTypeSelectorProps> = ({
  examType,
  studyCategory,
  gestationalAge,
  onExamTypeChange,
  onStudyCategoryChange,
  getDefaultCategory,
  suggestedLMP,
  pregnancyData,
  studyExists = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const examTypeOptions = [
    { value: 'OB', label: 'OB' },
    { value: 'Blind', label: 'Blind' },
    { value: 'GYN', label: 'GYN' },
    { value: 'General', label: 'General' },
  ];

  const examCategoryOptions = [
    { value: 'T1', label: 'Trimester 1' },
    { value: 'T2', label: 'Trimester 2' },
    { value: 'T3', label: 'Trimester 3' },
  ];

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="bg-card-color-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-title-page-color-100 dark:text-white">
            {/* @ts-ignore */}
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Select Exam Type and Study Category
          </h3>
          <button
            type="button"
            onClick={toggleCollapse}
            className="flex items-center justify-center p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label={isCollapsed ? 'Expand exam type selector' : 'Collapse exam type selector'}
          >
            {isCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="px-6 py-6 space-y-6">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-title-page-color-300 dark:text-gray-400">
              Select Exam Type:
            </label>
            <div className="grid grid-cols-4 gap-1 p-1 bg-card-color-200 dark:bg-gray-700 rounded-lg">
              {examTypeOptions.map((exam) => (
                <button
                  key={exam.value}
                  type="button"
                  onClick={() => onExamTypeChange(exam.value as any)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    examType === exam.value
                      ? 'bg-card-color-100 dark:bg-gray-600 text-title-page-color-100 dark:text-white shadow-sm'
                      : 'text-title-page-color-400 dark:text-gray-300 hover:text-title-page-color-100 dark:hover:text-white hover:bg-card-color-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {exam.label}
                </button>
              ))}
            </div>
          </div>

          {(examType === 'OB' || examType === 'Blind') && (
            <>
              {gestationalAge && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md p-3">
                  <div className="flex items-center gap-2 text-sm">
                    {/* @ts-ignore */}
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium text-blue-900 dark:text-blue-300">
                      Current Gestational Age: {gestationalAge.weeks} weeks,{' '}
                      {gestationalAge.days} days
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    Based on LMP, the suggested category is highlighted below
                  </p>
                </div>
              )}

              {!studyExists && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                    {/* @ts-ignore */}
                    <Calendar className="w-4 h-4" />
                    Select Study Category (Trimester):
                  </label>

                  <div className="grid grid-cols-3 gap-1 p-1 bg-card-color-200 dark:bg-gray-700 rounded-lg">
                    {examCategoryOptions.map((category) => {
                      const isRecommended =
                        gestationalAge &&
                        getDefaultCategory(
                          suggestedLMP || pregnancyData?.last_menstrual_period
                        ) === category.value;

                      return (
                        <button
                          key={category.value}
                          type="button"
                          onClick={() =>
                            onStudyCategoryChange(category.value as any)
                          }
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative ${
                            studyCategory === category.value
                              ? 'bg-card-color-100 dark:bg-gray-600 text-title-page-color-100 dark:text-white shadow-sm'
                              : 'text-title-page-color-400 dark:text-gray-300 hover:text-title-page-color-100 dark:hover:text-white hover:bg-card-color-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span>{category.label}</span>
                              {isRecommended && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-600 text-white ml-1">
                                  Rec
                                </span>
                              )}
                            </div>
                            <div className="text-xs opacity-75">
                              {category.value === 'T1'
                                ? '0-13 weeks'
                                : category.value === 'T2'
                                  ? '14-27 weeks'
                                  : '28+ weeks'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
